/**
 * Composes the instruction Seedance 2.5 receives for a timestamp-level video
 * edit, and validates the cues that feed it.
 *
 * Shared by the editor UI (which shows the composed prompt verbatim, so what
 * you see is what the model gets) and /api/edit-video (which recomposes it
 * server-side so API callers and a tampered client land on the same text).
 *
 * Two constraints from the ModelArk docs drive the shape of the output:
 *
 * 1. The task type is inferred from the prompt. A request carrying a
 *    `reference_video` is classified as reference-to-video, video editing, or
 *    video extension by looking for trigger words — editing needs one of
 *    "edit the video / add / delete / remove / modify / replace / change".
 *    Miss it and the task is misclassified and fails asynchronously with
 *    InvalidParameter.TaskTypeConstraint. The fixed "Video edit:" lead-in
 *    below is what guarantees the classification, whatever the user typed.
 * 2. Timestamps are honoured at whole-second resolution ("Seedance 2.5
 *    supports integer-second timestamps"), which is why cue boundaries are
 *    rounded rather than emitted with decimals the model would ignore.
 */

export interface VideoEditInstruction {
  /**
   * Whole-second window this instruction applies to. Both null means "the
   * whole clip" — used for global changes like removing the soundtrack.
   */
  startSeconds: number | null;
  endSeconds: number | null;
  text: string;
}

export const MAX_EDIT_INSTRUCTIONS = 6;
export const MAX_EDIT_INSTRUCTION_LENGTH = 400;
/** ModelArk caps reference images at 30; the editor stays deliberately small. */
export const MAX_EDIT_REFERENCE_IMAGES = 4;

const LEAD_IN =
  'Video edit: apply the changes listed below to @Video1 and change nothing else. Keep the same composition, camera moves, lighting, pacing, and audio as the original.';

/**
 * Lead-in for WaveSpeed's `video-edit` endpoint, which prepends its own
 * "Edit the input video." — the trigger word is already handled there, so ours
 * would only read as a duplicate.
 */
export const WAVESPEED_EDIT_LEAD_IN =
  'Apply the changes listed below and change nothing else. Keep the same composition, camera moves, lighting, pacing, and audio as the original.';

/** WAN 3.0's `video-edit` prepends nothing, so its lead-in names the task. */
export const WAN_30_EDIT_LEAD_IN =
  'Edit the input video: apply the changes listed below and change nothing else. Keep the same composition, camera moves, lighting, pacing, and audio as the original.';

const LEAD_INS = [LEAD_IN, WAVESPEED_EDIT_LEAD_IN, WAN_30_EDIT_LEAD_IN];
const REFERENCE_LINE =
  /^Reference images provided: @Image\d+(?:, @Image\d+)*\. Use them only where an instruction names them\.$/;
const WHOLE_CLIP_PREFIX = /^Throughout the video:\s*/;
const RANGE_PREFIX = /^From ((?:\d+m )?\d+s) to ((?:\d+m )?\d+s):\s*/;

/** Formats a whole second as "3s" / "1m 12s" for the prompt and the UI. */
export const formatEditSeconds = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
};

const isRangedInstruction = (
  instruction: VideoEditInstruction,
): instruction is VideoEditInstruction & {
  startSeconds: number;
  endSeconds: number;
} =>
  typeof instruction.startSeconds === 'number' &&
  typeof instruction.endSeconds === 'number' &&
  Number.isFinite(instruction.startSeconds) &&
  Number.isFinite(instruction.endSeconds);

const parseEditSeconds = (label: string): number => {
  const match = /^(?:(\d+)m )?(\d+)s$/.exec(label);
  return match ? Number(match[1] || 0) * 60 + Number(match[2]) : 0;
};

/**
 * Turns a prompt composed by `composeVideoEditPrompt` back into its cues.
 *
 * The gallery shows an edit's composed prompt, so copying it into a cue is the
 * obvious way to run an edit again. Taken literally that wraps it twice: the
 * lead-in and reference line come back as an instruction, and the cue's
 * character cap cuts off the real instruction (row 5429482 reached the model
 * ending in "Both animals"). Returns null for anything that doesn't start with
 * one of our lead-ins, so typed instructions are never reinterpreted.
 */
export function parseComposedVideoEditPrompt(
  text: string,
): VideoEditInstruction[] | null {
  const trimmed = String(text ?? '').trim();
  if (!LEAD_INS.some((leadIn) => trimmed.startsWith(leadIn))) return null;

  const instructions: VideoEditInstruction[] = [];
  for (const rawLine of trimmed.split(/\r?\n/)) {
    let line = rawLine.trim();
    let startsCue = false;
    let range: { startSeconds: number; endSeconds: number } | null = null;
    // A composed prompt pasted into a cue and composed again nests the
    // prefixes ("Throughout the video: Video edit: …"), so peel them all.
    for (;;) {
      const whole = WHOLE_CLIP_PREFIX.exec(line);
      const ranged = whole ? null : RANGE_PREFIX.exec(line);
      const prefix = whole ?? ranged;
      if (!prefix) break;
      startsCue = true;
      if (ranged && !range) {
        range = {
          startSeconds: parseEditSeconds(ranged[1]),
          endSeconds: parseEditSeconds(ranged[2]),
        };
      }
      line = line.slice(prefix[0].length).trim();
    }
    if (!line || LEAD_INS.includes(line) || REFERENCE_LINE.test(line)) {
      continue;
    }

    const previous = instructions[instructions.length - 1];
    if (!startsCue && previous) {
      // A cue's own text can span several lines.
      previous.text = `${previous.text}\n${line}`;
    } else {
      instructions.push({
        startSeconds: range?.startSeconds ?? null,
        endSeconds: range?.endSeconds ?? null,
        text: line,
      });
    }
  }
  return instructions;
}

/**
 * Drops empty cues, rounds boundaries to whole seconds, clamps them into the
 * clip, and orders them along the timeline (whole-clip cues last, so the model
 * reads the targeted changes first). A cue holding a pasted composed prompt is
 * split back into the cues it came from first; the ones without a window of
 * their own keep the window of the cue they were pasted into.
 */
export function normalizeVideoEditInstructions(
  instructions: VideoEditInstruction[],
  durationSeconds: number,
): VideoEditInstruction[] {
  const maxSecond = Math.max(1, Math.round(durationSeconds));

  const cleaned = (instructions || [])
    .flatMap((instruction): VideoEditInstruction[] => {
      const parsed = parseComposedVideoEditPrompt(instruction?.text);
      if (!parsed) return [instruction];
      return parsed.map((cue) =>
        isRangedInstruction(cue)
          ? cue
          : {
              ...cue,
              startSeconds: instruction?.startSeconds ?? null,
              endSeconds: instruction?.endSeconds ?? null,
            },
      );
    })
    .map((instruction): VideoEditInstruction | null => {
      const text = String(instruction?.text ?? '')
        .trim()
        .slice(0, MAX_EDIT_INSTRUCTION_LENGTH);
      if (!text) return null;

      if (!isRangedInstruction(instruction)) {
        return { startSeconds: null, endSeconds: null, text };
      }

      const start = Math.min(
        Math.max(0, Math.round(instruction.startSeconds)),
        maxSecond - 1,
      );
      const end = Math.min(
        Math.max(start + 1, Math.round(instruction.endSeconds)),
        maxSecond,
      );
      // A cue spanning the whole clip reads better as "throughout".
      if (start === 0 && end >= maxSecond) {
        return { startSeconds: null, endSeconds: null, text };
      }
      return { startSeconds: start, endSeconds: end, text };
    })
    .filter((instruction): instruction is VideoEditInstruction =>
      Boolean(instruction),
    );

  return cleaned
    .slice(0, MAX_EDIT_INSTRUCTIONS)
    .sort(
      (a, b) => (a.startSeconds ?? Infinity) - (b.startSeconds ?? Infinity),
    );
}

/** Human-readable label for a cue's window, e.g. "0s – 3s" or "Whole clip". */
export function describeEditRange(instruction: VideoEditInstruction): string {
  if (!isRangedInstruction(instruction)) return 'Whole clip';
  return `${formatEditSeconds(instruction.startSeconds)} – ${formatEditSeconds(
    instruction.endSeconds,
  )}`;
}

export interface ComposeVideoEditPromptOptions {
  /** How many reference images are attached, so they can be introduced by tag. */
  referenceImageCount?: number;
  /** Overrides the opening line (see WAVESPEED_EDIT_LEAD_IN). */
  leadIn?: string;
}

/**
 * Builds the final instruction. Already-normalized cues are expected — call
 * `normalizeVideoEditInstructions` first.
 */
export function composeVideoEditPrompt(
  instructions: VideoEditInstruction[],
  options: ComposeVideoEditPromptOptions = {},
): string {
  const lines: string[] = [options.leadIn || LEAD_IN];

  const referenceImageCount = Math.min(
    Math.max(0, Math.floor(options.referenceImageCount ?? 0)),
    MAX_EDIT_REFERENCE_IMAGES,
  );
  if (referenceImageCount > 0) {
    const tags = Array.from(
      { length: referenceImageCount },
      (_, index) => `@Image${index + 1}`,
    ).join(', ');
    lines.push(
      `Reference images provided: ${tags}. Use them only where an instruction names them.`,
    );
  }

  for (const instruction of instructions) {
    if (isRangedInstruction(instruction)) {
      lines.push(
        `From ${formatEditSeconds(
          instruction.startSeconds,
        )} to ${formatEditSeconds(instruction.endSeconds)}: ${instruction.text}`,
      );
    } else {
      lines.push(`Throughout the video: ${instruction.text}`);
    }
  }

  return lines.join('\n');
}

/**
 * Returns an error message when the cues can't produce a valid edit task, or
 * null when they're good to submit.
 */
export function validateVideoEditInstructions(
  instructions: VideoEditInstruction[],
): string | null {
  if (!Array.isArray(instructions) || instructions.length === 0) {
    return 'Add at least one edit describing what should change.';
  }
  if (instructions.length > MAX_EDIT_INSTRUCTIONS) {
    return `You can apply up to ${MAX_EDIT_INSTRUCTIONS} edits in one pass.`;
  }
  if (instructions.some((instruction) => !instruction.text.trim())) {
    return 'Every edit needs a description of what should change.';
  }
  return null;
}
